import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import StoreBenefits from 'components/Blocks/Store/StoreBenefits';
import useTranslation from 'next-translate/useTranslation';
import { StoreLocatorStoreType, StoreServiceIconsType } from 'types/store';
import twMerge from 'utils/twMerge';

type StoreGroupSectionProps = {
    title: string;
    description: string;
    stores: StoreLocatorStoreType[];
    selectedStoreUuid: string | null;
    serviceIcons: StoreServiceIconsType;
    onSelectStore: (store: StoreLocatorStoreType) => void;
    removeBottomMargin?: boolean;
};

export const StoreGroupSection: FC<StoreGroupSectionProps> = ({
    title,
    description,
    stores,
    selectedStoreUuid,
    serviceIcons,
    onSelectStore,
    removeBottomMargin,
}) => {
    const { t } = useTranslation();

    if (stores.length === 0) {
        return null;
    }

    return (
        <>
            <h2>{title}</h2>
            <p>{description}</p>
            <ul className={twMerge('mb-[60px] mt-5', removeBottomMargin && 'mb-0')}>
                {stores.map((store) => (
                    <li
                        key={store.uuid}
                        className="-mx-5 border-t border-gray px-5 last:border-b lg:mx-0 lg:grid lg:grid-cols-[1fr_300px] lg:gap-x-5 lg:px-0"
                        data-store-list-item-id={store.uuid}
                    >
                        <ExtendedNextLink
                            className="block py-5 text-primary no-underline hover:text-red hover:no-underline lg:grid lg:grid-cols-3 lg:gap-x-5 lg:py-3 vl:gap-x-[60px]"
                            href={store.slug}
                            type="store"
                        >
                            <>
                                <h3 className="text-current text-body font-medium">{store.name}</h3>
                                <div className="text-body font-normal">{store.street}</div>
                                <div className="text-small font-medium">{store.city}</div>
                            </>
                        </ExtendedNextLink>
                        <div className="flex items-center justify-between pb-5 lg:justify-end lg:pb-0">
                            <StoreBenefits
                                acceptGiftCoupon={store.acceptGiftCoupon}
                                acceptGiftCouponIcon={serviceIcons.acceptGiftCouponIcon}
                                hasLoyaltyProgram={store.hasLoyaltyProgram}
                                hasLoyaltyProgramIcon={serviceIcons.hasLoyaltyProgramIcon}
                                isPickupPoint={store.isPickupPoint}
                                isPickupPointIcon={serviceIcons.isPickupPointIcon}
                            />
                            {store.isPickupPoint ? (
                                <button
                                    aria-pressed={selectedStoreUuid === store.uuid}
                                    type="button"
                                    className={twMerge(
                                        'group relative ml-5 flex h-full cursor-pointer items-center whitespace-nowrap border-0 bg-transparent pl-[25px] text-body hover:text-green',
                                        selectedStoreUuid === store.uuid && 'ml-[19px] font-bold text-green',
                                    )}
                                    onClick={() => onSelectStore(store)}
                                >
                                    <span className="absolute left-0 top-1/2 flex h-[18px] w-[18px] -translate-y-1/2 items-center justify-center rounded-full border border-black group-hover:border-green">
                                        {selectedStoreUuid === store.uuid && (
                                            <span className="h-2 w-2 rounded-full bg-green" />
                                        )}
                                    </span>
                                    {t('My store')}
                                </button>
                            ) : (
                                <div className="ml-5 h-full w-[114px] lg:w-[126px]" />
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </>
    );
};
